import { useReactiveVar } from '@apollo/client/react';
import useAuthWithCognito from 'hooks/useAuthWithCognito/useAuth';
import { Navigate, Route, Routes } from 'react-router';
import { authVar } from 'state/vars';
import AppLayout from 'v2/layout/AppLayout/AppLayout';
import Loading from 'v2/layout/Loading/Loading';
import AccountSettings from 'v2/pages/AccountSettings/AccountSettings';
import Dashboard from 'v2/pages/Dashboard/Dashboard';
import NoAccess from 'v2/pages/GeneralErrors/NoAccess';
import NotFound from 'v2/pages/GeneralErrors/NotFound';
import SomethingWentWrong from 'v2/pages/GeneralErrors/SomethingWentWrong';
import Login from 'v2/pages/Login/Login';
import MyProfile from 'v2/pages/MyProfile/MyProfile';
import Users from 'v2/pages/Users/Users';
import Visits from 'v2/pages/Visits/Visits';
import { areQrCodeVisitsEnabled } from 'v2/utils/areQrCodeVisitsEnabled';
import { isSecurityEnabled } from 'v2/utils/isSecurityEnabled';
import ApiKeys from './pages/ApiKeys/ApiKeys';
import Cards from './pages/Cards/Cards';
import Devices from './pages/Devices/Devices';
import Events from './pages/Events/Events';
import ForgotPassword from './pages/ForgotPassword/ForgotPassword';
import LandingPage from './pages/LandingPage/LandingPage';
import LicensePortalPage from './pages/Onboarding/LicensePortalPage';
import ResetOrSetPassword from './pages/ResetOrSetPassword/ResetOrSetPassword';

const isDashboardEnabled = process.env.REACT_APP_IS_DASHBOARD_ENABLED === 'true';

const landingRoutes = (
  <>
    <Route path="/landing/access" element={<LandingPage pageVariant="ACCESS" />} />
    <Route path="/landing/config" element={<LandingPage pageVariant="CONFIG" />} />
  </>
);

const passwordReset = <Route path="/reset-password" element={<ResetOrSetPassword pageVariant="RESET" />} />;

const NonAuthenticatedRoutes = () => {
  return (
    <Routes>
      <Route path="/" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/set-password" element={<ResetOrSetPassword pageVariant="SET" />} />
      <Route path="/connect-to-atlas" element={<LicensePortalPage />} />
      {passwordReset}
      {landingRoutes}
      <Route path="/*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

interface RoutesProps {
  securityEnabled: boolean;
}

const AdminRoutes: React.FC<RoutesProps> = ({ securityEnabled }) => {
  return (
    <AppLayout>
      <Routes>
        <Route path="/" element={<Navigate to={isDashboardEnabled ? '/dashboard' : '/users'} replace />} />
        {isDashboardEnabled && <Route path="/dashboard" element={<Dashboard />} />}
        <Route path="/users/*" element={<Users />} />
        {areQrCodeVisitsEnabled() && <Route path="/visits/*" element={<Visits />} />}
        <Route path="/devices/*" element={<Devices />} />
        <Route path="/events/*" element={<Events />} />
        <Route path="/cards/*" element={<Cards />} />
        <Route path="/api-keys/*" element={<ApiKeys />} />
        <Route path="/my-profile/*" element={<MyProfile />} />
        <Route path="/account-settings" element={securityEnabled ? <AccountSettings /> : <NoAccess />} />
        {passwordReset}
        {landingRoutes}
        <Route path="/*" element={<NotFound />} />
      </Routes>
    </AppLayout>
  );
};

const UserRoutes: React.FC<RoutesProps> = ({ securityEnabled }) => {
  return (
    <AppLayout>
      <Routes>
        <Route path="/" element={<Navigate to="/my-profile" replace />} />
        <Route path="/my-profile/*" element={<MyProfile />} />
        <Route path="/account-settings" element={securityEnabled ? <AccountSettings /> : <NoAccess />} />
        {landingRoutes}
        <Route path="/*" element={<NotFound />} />
      </Routes>
    </AppLayout>
  );
};

const Router: React.FC<React.PropsWithChildren> = () => {
  const { isUserAuthenticated, isLoading, hasAuthError, retryAuth } = useAuthWithCognito();
  const { user } = useReactiveVar(authVar);
  const securityEnabled = isSecurityEnabled(user);

  // Must come before isLoading: a failed Cognito init leaves isLoading true indefinitely.
  if (hasAuthError) {
    return <SomethingWentWrong fullPage onRetry={retryAuth} />;
  }

  if (isLoading) {
    return <Loading />;
  }

  if (isUserAuthenticated && user?.isAdmin) {
    return <AdminRoutes securityEnabled={securityEnabled} />;
  }

  if (isUserAuthenticated) {
    return <UserRoutes securityEnabled={securityEnabled} />;
  }

  return <NonAuthenticatedRoutes />;
};

export default Router;
